import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { RenderIf } from 'react-rainbow-components';
import { isDefined, isEmpty } from '@rainbow-modules/validation';
import Button from 'components/Button';
import CalendarIcon from 'components/icons/calendarThin';
import CircleWithDotIcon from 'components/icons/circleWithDot';
import GaugeIcon from 'components/icons/gauge';
import Dollar from 'components/icons/dollar';
import ButtonIcon from 'components/ButtonIcon';
import { TagIcon } from 'components/Tags/styled';
import Close from 'components/icons/close';
import Gradient from 'components/Gradient';
import User from 'components/icons/user';
import { ItemUser } from 'data/firestore/globals';
import tryParseNumber from 'data/services/string/parseNumber';
import useSearchFilters from 'hooks/useSearchFilters';
import Accordion from './accordion';
import {
    FilterHeader,
    Title,
    StyledDatePickerFilter,
    InlineTodoCalendar,
    InlineTodoCalendarContainer,
    InlineCalendarHeader,
    StyledCheckboxGroup,
    StyledTeamMemberSelect,
    StyledPipelineStageSelect,
    StyledPriceFilter,
    FiltersContainer,
    FiltersContent,
    StyledEntityTagsFilter,
} from './styled';

interface Params {
    agentId: string;
    pipelineId: string;
    viewType?: 'board' | 'list' | 'todos';
    showCloseButton?: boolean;
    onRequestClose?: () => void;
}

const opportunityFilterNames = ['createdAt', 'owner', 'stage', 'score', 'amount', 'tags'];
const todoFilterNames = ['todoDueAt', 'todoAssignee', 'todoPriority', 'todoStatus'];
const allFilterNames = [...opportunityFilterNames, ...todoFilterNames];

const scoreFilterOptions = [
    { label: 'High (70 - 100)', value: 'high' },
    { label: 'Medium (40 - 69)', value: 'medium' },
    { label: 'Low (0 - 39)', value: 'low' },
    { label: 'Not Scored', value: 'not-scored' },
];

const todoPriorityOptions = [
    { label: 'High', value: 'high' },
    { label: 'Medium', value: 'medium' },
    { label: 'Low', value: 'low' },
];

const todoStatusOptions = [
    { label: 'To do', value: 'pending' },
    { label: 'In progress', value: 'in_progress' },
    { label: 'Done', value: 'done' },
];

const Filters = ({
    agentId,
    pipelineId,
    viewType = 'board',
    showCloseButton = true,
    onRequestClose = () => {},
}: Params) => {
    const isTodosView = viewType === 'todos';
    const activeFilterNames = isTodosView ? todoFilterNames : opportunityFilterNames;

    const {
        values,
        getValue: getFilterValue,
        setValue: setFilterValue,
    } = useSearchFilters({ filterNames: allFilterNames });

    const activeFiltersCount = useMemo(
        () => activeFilterNames.reduce(
            (count, key) => (
                getFilterValue(key) !== null
                    ? count + 1
                    : count
            ),
            0,
        ),
        [activeFilterNames, getFilterValue],
    );

    const createdAtFilterValue = useMemo(
        () => {
            const { createdAt } = values;
            if (!createdAt) return undefined;
            return String(createdAt).split(',').map((strDate) => DateTime.fromISO(strDate).toJSDate());
        },
        [values],
    );

    const handleChangeCreatedAt = useCallback(
        (newFilterValue: Date[]) => setFilterValue(
            'createdAt',
            (
                newFilterValue.length === 0
                    ? null
                    : newFilterValue.map((date) => date.toISOString()).join(',')
            ),
        ),
        [setFilterValue],
    );

    const handleChangeOwner = useCallback(
        (newFilterValue: Array<ItemUser | null> | null) => setFilterValue(
            'owner',
            (
                !newFilterValue || isEmpty(newFilterValue)
                    ? null
                    : newFilterValue.map((user) => user?.uid as string).join(',')
            ),
        ),
        [setFilterValue],
    );

    const ownerFilterValue = useMemo(
        () => {
            const { owner } = values;
            if (owner !== null) return String(owner).split(',');
            return null;
        },
        [values],
    );

    const handleChangeStage = useCallback(
        (newFilterValue: string[] | null) => setFilterValue(
            'stage',
            (
                !newFilterValue || isEmpty(newFilterValue)
                    ? null
                    : newFilterValue.join(',')
            ),
        ),
        [setFilterValue],
    );

    const stageFilterValue = useMemo(
        () => {
            const { stage } = values;
            if (stage !== null) return String(stage).split(',');
            return null;
        },
        [values],
    );

    const handleChangeScore = useCallback(
        (newFilterValue: string[]) => setFilterValue(
            'score',
            !newFilterValue || isEmpty(newFilterValue)
                ? null
                : newFilterValue.join(','),
        ),
        [setFilterValue],
    );

    const scoreFilterValue = useMemo(
        () => {
            const { score } = values;
            const allFilterOptionValues = scoreFilterOptions.map((option) => option.value);
            if (!isDefined(score)) return [];
            return String(score).split(',').reduce(
                (result: string[], value: string) => {
                    if (allFilterOptionValues.includes(value)) {
                        return [...result, value];
                    }

                    return result;
                },
                [],
            );
        },
        [values],
    );

    const handleChangeAmount = useCallback(
        (newFilterValue: Array<number | null>) => {
            setFilterValue(
                'amount',
                newFilterValue.every((value) => isEmpty(value))
                    ? null
                    : newFilterValue.map((value) => value || '').join(','),
            );
        },
        [setFilterValue],
    );

    const amountFilterValue = useMemo(
        () => {
            const { amount } = values;
            if (amount) {
                return String(amount).split(',').reduce(
                    (
                        result: Array<number | null>,
                        value: string,
                    ) => [...result, tryParseNumber(value)],
                    [],
                );
            }

            return undefined;
        },
        [values],
    );

    const tagsFilterValue = values.tags !== null ? String(values.tags).split(',') : null;

    const handleChangeTagsFilter = useCallback(
        (newFilterValue: string[] | null) => setFilterValue(
            'tags',
            (
                newFilterValue === null
                    ? null
                    : newFilterValue.join(',')
            ),
        ),
        [setFilterValue],
    );

    const todoDueAtFilterValue = useMemo(
        () => {
            const { todoDueAt } = values;
            if (!todoDueAt) return undefined;
            return String(todoDueAt).split(',').map((strDate) => DateTime.fromISO(strDate).toJSDate());
        },
        [values],
    );

    const handleChangeTodoDueAt = useCallback(
        (newFilterValue: Date) => {
            const selectedDate = DateTime.fromJSDate(newFilterValue);
            setFilterValue(
                'todoDueAt',
                [
                    selectedDate.startOf('day').toISO(),
                    selectedDate.endOf('day').toISO(),
                ].join(','),
            );
        },
        [setFilterValue],
    );

    const handleChangeTodoAssignee = useCallback(
        (newFilterValue: Array<ItemUser | null> | null) => setFilterValue(
            'todoAssignee',
            (
                !newFilterValue || isEmpty(newFilterValue)
                    ? null
                    : newFilterValue.map((user) => user?.uid as string).join(',')
            ),
        ),
        [setFilterValue],
    );

    const todoAssigneeFilterValue = useMemo(
        () => {
            const { todoAssignee } = values;
            if (todoAssignee !== null) return String(todoAssignee).split(',');
            return null;
        },
        [values],
    );

    const handleChangeTodoPriority = useCallback(
        (newFilterValue: string[]) => setFilterValue(
            'todoPriority',
            !newFilterValue || isEmpty(newFilterValue)
                ? null
                : newFilterValue.join(','),
        ),
        [setFilterValue],
    );

    const todoPriorityFilterValue = useMemo(
        () => {
            const { todoPriority } = values;
            const allFilterOptionValues = todoPriorityOptions.map((option) => option.value);
            if (!isDefined(todoPriority)) return [];
            return String(todoPriority).split(',').reduce(
                (result: string[], value: string) => {
                    if (allFilterOptionValues.includes(value)) {
                        return [...result, value];
                    }
                    return result;
                },
                [],
            );
        },
        [values],
    );

    const handleChangeTodoStatus = useCallback(
        (newFilterValue: string[]) => setFilterValue(
            'todoStatus',
            !newFilterValue || isEmpty(newFilterValue)
                ? null
                : newFilterValue.join(','),
        ),
        [setFilterValue],
    );

    const todoStatusFilterValue = useMemo(
        () => {
            const { todoStatus } = values;
            const allFilterOptionValues = todoStatusOptions.map((option) => option.value);
            if (!isDefined(todoStatus)) return [];
            return String(todoStatus).split(',').reduce(
                (result: string[], value: string) => {
                    if (allFilterOptionValues.includes(value)) {
                        return [...result, value];
                    }
                    return result;
                },
                [],
            );
        },
        [values],
    );

    const handleClearFilters = useCallback(
        () => {
            activeFilterNames.forEach((filterName) => setFilterValue(filterName, null));
        },
        [activeFilterNames, setFilterValue],
    );

    return (
        <FiltersContainer>
            <FilterHeader>
                <Title>
                    Filters
                    {' '}
                    <RenderIf isTrue={activeFiltersCount > 0}>
                        (
                        {activeFiltersCount}
                        )
                    </RenderIf>
                </Title>
                <div>
                    <Button
                        label="Clear"
                        variant="base"
                        size="small"
                        disabled={!activeFiltersCount}
                        borderRadius="semi-square"
                        onClick={handleClearFilters}
                    />
                    <RenderIf isTrue={showCloseButton}>
                        <ButtonIcon
                            borderRadius="semi-square"
                            size="small"
                            icon={<Close />}
                            onClick={onRequestClose}
                        />
                    </RenderIf>
                </div>
            </FilterHeader>
            <FiltersContent>
                <RenderIf isTrue={!isTodosView}>
                    <>
                        <Accordion icon={<CalendarIcon />} label="Created Date" name="createdAt">
                            <StyledDatePickerFilter
                                onChange={handleChangeCreatedAt}
                                value={createdAtFilterValue}
                            />
                        </Accordion>
                        <Accordion icon={<User />} label="Owner" name="owner">
                            <StyledTeamMemberSelect
                                hideLabel
                                agentId={agentId}
                                value={ownerFilterValue}
                                onChange={handleChangeOwner}
                            />
                        </Accordion>
                        <Accordion icon={<CircleWithDotIcon />} label="Pipeline Stage" name="stage">
                            <StyledPipelineStageSelect
                                agentId={agentId}
                                pipelineId={pipelineId}
                                value={stageFilterValue}
                                onChange={handleChangeStage}
                            />
                        </Accordion>
                        <Accordion icon={<GaugeIcon />} label="Opportunity Score" name="score">
                            <StyledCheckboxGroup
                                value={scoreFilterValue}
                                options={scoreFilterOptions}
                                onChange={handleChangeScore}
                            />
                        </Accordion>
                        <Accordion icon={<Dollar />} label="Amount" name="amount">
                            <StyledPriceFilter
                                value={amountFilterValue}
                                onChange={handleChangeAmount}
                            />
                        </Accordion>
                        <Accordion icon={<TagIcon />} label="Tags" name="tags">
                            <StyledEntityTagsFilter
                                agentId={agentId}
                                value={tagsFilterValue}
                                onChange={handleChangeTagsFilter}
                                entity="opportunity"
                            />
                        </Accordion>
                    </>
                </RenderIf>
                <RenderIf isTrue={isTodosView}>
                    <>
                        <InlineTodoCalendarContainer>
                            <InlineCalendarHeader>
                                <CalendarIcon />
                                Due Date
                            </InlineCalendarHeader>
                            <InlineTodoCalendar
                                value={todoDueAtFilterValue?.[0] || new Date()}
                                onChange={handleChangeTodoDueAt}
                            />
                        </InlineTodoCalendarContainer>
                        <Accordion icon={<CircleWithDotIcon />} label="Status" name="todoStatus">
                            <StyledCheckboxGroup
                                value={todoStatusFilterValue}
                                options={todoStatusOptions}
                                onChange={handleChangeTodoStatus}
                            />
                        </Accordion>
                        <Accordion icon={<GaugeIcon />} label="Priority" name="todoPriority">
                            <StyledCheckboxGroup
                                value={todoPriorityFilterValue}
                                options={todoPriorityOptions}
                                onChange={handleChangeTodoPriority}
                            />
                        </Accordion>
                        <Accordion icon={<User />} label="Assignee" name="todoAssignee">
                            <StyledTeamMemberSelect
                                hideLabel
                                agentId={agentId}
                                value={todoAssigneeFilterValue}
                                onChange={handleChangeTodoAssignee}
                            />
                        </Accordion>
                    </>
                </RenderIf>
            </FiltersContent>
            <Gradient position="bottom" />
        </FiltersContainer>
    );
};

export default Filters;
